import { Box, Button, FormControl } from '@mui/material';
import { Field, useFormikContext } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { SelectionAutocomplete } from 'components/Autocomplete/SelectionAutocomplete';
import { FormTextField } from 'components/TextField';
import {
    getAssociatedViewAssetsPicklist,
    getAssociatedViewItemsPicklist,
    getAssociatedViewZonesPicklist,
    resetAssociatedViewAssetsPicklistStatus,
    resetAssociatedViewItemsPicklistStatus,
    resetAssociatedViewZonesPicklistStatus,
    selectAssociatedViewAssetsPicklist,
    selectAssociatedViewAssetsPicklistLoadingStatus,
    selectAssociatedViewItemsPicklist,
    selectAssociatedViewItemsPicklistLoadingStatus,
    selectAssociatedViewZonesPicklist,
    selectAssociatedViewZonesPicklistLoadingStatus,
} from 'features/picklists/pickListsSlice';
import { CheckboxWithLabel } from 'formik-mui';
import { AssetTypes } from 'GeminiViewerComponent/helpers';
import { itemService, zoneService } from 'features/services';
import { useEffect } from 'react';
import { loadZone } from 'shared/loadZoneSlice';
import { setSelectedSearchItemId } from 'GeminiViewerComponent/features/asset/assetSlice';

const AssociatedView = ({ asset_id, zone_id, item_id, close_associated_view, node, initial_view, setInitialView }) => {
    const dispatch = useDispatch();
    const { setFieldValue } = useFormikContext();
    const associatedViewZones = useSelector(selectAssociatedViewZonesPicklist);
    const associatedViewItems = useSelector(selectAssociatedViewItemsPicklist);

    const handleAssetChange = async (newAsset, setFormFieldValue) => {
        setFormFieldValue('close_associated_view', false);

        const selectedZoneName = associatedViewZones.find((zone) => zone.zone_id === zone_id)?.display_name;
        const selectedItemName = associatedViewItems.find((item) => item.item_id === item_id)?.display_name;
        const newAssetId = newAsset?.asset_id;

        if (!newAssetId || !selectedZoneName) {
            setFormFieldValue('zone_id', null);
            setFormFieldValue('item_id', null);
            dispatch(resetAssociatedViewZonesPicklistStatus());
            dispatch(resetAssociatedViewItemsPicklistStatus());
            return;
        }

        try {
            const matchedZone = await zoneService.getByName(newAssetId, selectedZoneName);
            const matchedZoneId = matchedZone?.zone_id ?? null;

            if (!matchedZoneId) {
                setFormFieldValue('zone_id', null);
                setFormFieldValue('item_id', null);
                dispatch(resetAssociatedViewZonesPicklistStatus());
                dispatch(resetAssociatedViewItemsPicklistStatus());
                return;
            }

            setFormFieldValue('zone_id', matchedZoneId);
            dispatch(getAssociatedViewZonesPicklist({ assetId: newAssetId }));

            if (!selectedItemName) {
                setFormFieldValue('item_id', null);
                dispatch(resetAssociatedViewItemsPicklistStatus());
                return;
            }

            try {
                const matchedItem = await itemService.getByName(matchedZoneId, selectedItemName);
                const matchedItemId = matchedItem?.item_id ?? null;
                setFormFieldValue('item_id', matchedItemId);
                // Fetch items for the matched zone so the autocomplete can display the selection
                dispatch(getAssociatedViewItemsPicklist({ zoneId: matchedZoneId }));
            } catch {
                setFormFieldValue('item_id', null);
                dispatch(resetAssociatedViewItemsPicklistStatus());
            }
        } catch {
            setFormFieldValue('zone_id', null);
            setFormFieldValue('item_id', null);
            dispatch(resetAssociatedViewZonesPicklistStatus());
            dispatch(resetAssociatedViewItemsPicklistStatus());
        }
    };

    useEffect(() => {
        dispatch(resetAssociatedViewAssetsPicklistStatus());
        dispatch(resetAssociatedViewZonesPicklistStatus());
        dispatch(resetAssociatedViewItemsPicklistStatus());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (node?.asset_id) {
            dispatch(getAssociatedViewZonesPicklist({ assetId: node?.asset_id }));
            if (node?.zone_id) {
                dispatch(getAssociatedViewItemsPicklist({ zoneId: node?.zone_id }));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [node]);
    return (
        <Box component="fieldset" border="1px solid #cccccc" borderRadius="5px">
            <Box component="legend" marginLeft="10px" padding="3px" fontSize="14px" color="#6d7178">
                Associated View
            </Box>
            <Box display="flex" alignItems="center" flexDirection="column" gap="5px" width="100%">
                {!asset_id && !zone_id && !item_id && (
                    <FormControl sx={{ padding: '0 8px' }} fullWidth>
                        <FormTextField
                            component={CheckboxWithLabel}
                            Label={{
                                label: 'Close associated view',
                            }}
                            label="Close associated view"
                            type="checkbox"
                            checked={close_associated_view}
                            onChange={(evt) => setFieldValue('close_associated_view', evt.target.checked)}
                        />
                    </FormControl>
                )}
                <FormControl sx={{ padding: '0 8px' }} fullWidth>
                    <Field name="asset_id">
                        {({ field: { value }, form: { setFieldValue } }) => (
                            <SelectionAutocomplete
                                title="Asset"
                                keyProperty="asset_id"
                                nameProperty="display_name"
                                entityIds={value}
                                setEntityIds={setFieldValue}
                                onCustomChange={(newValue) => handleAssetChange(newValue, setFieldValue)}
                                entitySelector={(state) =>
                                    selectAssociatedViewAssetsPicklist(state).filter(
                                        (asset) => asset.asset_type_id !== AssetTypes.Procedure
                                    )
                                }
                                fetchEntityPage={getAssociatedViewAssetsPicklist}
                                formField={'asset_id'}
                                multiSelection={false}
                                entityStatusSelector={selectAssociatedViewAssetsPicklistLoadingStatus}
                            />
                        )}
                    </Field>
                </FormControl>
                {asset_id && (
                    <FormControl sx={{ padding: '0 8px' }} fullWidth>
                        <Field name="zone_id">
                            {({ field: { value }, form: { setFieldValue } }) => (
                                <SelectionAutocomplete
                                    title="Zone"
                                    keyProperty="zone_id"
                                    nameProperty="display_name"
                                    entityIds={value}
                                    setEntityIds={setFieldValue}
                                    onCustomChange={() => {
                                        dispatch(resetAssociatedViewItemsPicklistStatus());
                                        setFieldValue('close_associated_view', false);
                                        setFieldValue('item_id', null);
                                        dispatch(setSelectedSearchItemId(null));
                                    }}
                                    entitySelector={selectAssociatedViewZonesPicklist}
                                    fetchEntityPage={getAssociatedViewZonesPicklist}
                                    formField={'zone_id'}
                                    multiSelection={false}
                                    entityStatusSelector={selectAssociatedViewZonesPicklistLoadingStatus}
                                    fetchParams={{
                                        assetId: asset_id,
                                        includeChildren: false,
                                    }}
                                />
                            )}
                        </Field>
                    </FormControl>
                )}
                {asset_id && zone_id && (
                    <FormControl sx={{ padding: '0 8px' }} fullWidth>
                        <Field name="item_id">
                            {({ field: { value }, form: { setFieldValue } }) => (
                                <>
                                    <SelectionAutocomplete
                                        title="Item"
                                        keyProperty="item_id"
                                        nameProperty="display_name"
                                        entityIds={value}
                                        setEntityIds={setFieldValue}
                                        onCustomChange={async (newValue) => {
                                            // keep form updated
                                            setFieldValue('item_id', newValue ? newValue.item_id : null);
                                            setFieldValue('close_associated_view', false);

                                            // if an item is chosen, load its zone (safely) and highlight the item in the viewer
                                            if (newValue && newValue.item_id) {
                                                try {
                                                    await dispatch(
                                                        loadZone({
                                                            zoneId: zone_id,
                                                        })
                                                    ).unwrap();
                                                } catch {
                                                    /* ignore load errors */
                                                }
                                                dispatch(setSelectedSearchItemId(newValue.item_id));
                                            } else {
                                                // clear highlight when no item selected
                                                dispatch(setSelectedSearchItemId(null));
                                            }
                                        }}
                                        entitySelector={selectAssociatedViewItemsPicklist}
                                        fetchEntityPage={getAssociatedViewItemsPicklist}
                                        formField="item_id"
                                        multiSelection={false}
                                        entityStatusSelector={selectAssociatedViewItemsPicklistLoadingStatus}
                                        fetchParams={{ zoneId: zone_id }}
                                    />
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        sx={{ mt: 1 }}
                                        onClick={() => {
                                            setInitialView({ asset_id, zone_id, item_id: value, initial_view });
                                        }}
                                    >
                                        Set initial view
                                    </Button>
                                </>
                            )}
                        </Field>
                    </FormControl>
                )}
            </Box>
        </Box>
    );
};

export { AssociatedView };
